import { Injectable, inject } from '@angular/core';
import { map, mergeMap, Observable, of } from 'rxjs';
import { ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CartDto } from '@mkp/publication/models';
import { BillingAddress } from '@mkp/shared/models';

@Injectable({ providedIn: 'root' })
export class CartResource {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT_PROVIDER);
  private readonly ECOM_HEADERS = new HttpHeaders({
    Accept: 'application/json',
  });
  private readonly ECOM_PATCH_HEADERS = new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/merge-patch+json',
  });

  get(cartTokenValue: string): Observable<CartDto> {
    return this.http.get<CartDto>(`${this.environment.api.ecom}/orders/${cartTokenValue}`, {
      headers: this.ECOM_HEADERS,
    });
  }

  createCartWithProduct(
    accountId: string,
    variantCode: string,
    quantity: number
  ): Observable<CartDto> {
    return this.createCart(accountId).pipe(
      mergeMap((cartTokenValue) => this.addProductToCart(cartTokenValue, variantCode, quantity))
    );
  }

  replaceProductInCart(
    cartTokenValue: string,
    orderItemId: string,
    variantCode: string,
    quantity: number
  ): Observable<CartDto> {
    return this.removeProductFromCart(cartTokenValue, orderItemId).pipe(
      mergeMap(() => this.addProductToCart(cartTokenValue, variantCode, quantity))
    );
  }

  updateProductQuantityInCart(
    cartTokenValue: string,
    orderItemId: string,
    quantity: number
  ): Observable<CartDto> {
    return this.http.patch<CartDto>(
      `${this.environment.api.ecom}/orders/${cartTokenValue}/items/${orderItemId}`,
      {
        quantity,
      },
      {
        headers: this.ECOM_PATCH_HEADERS,
      }
    );
  }

  upsertCouponInCart(
    cartTokenValue: string,
    coupon: string | null,
    billingAddress: BillingAddress,
    userClientId: string,
    walletId?: string
  ): Observable<CartDto> {
    return this.http.put<CartDto>(
      `${this.environment.api.ecom}/orders/${cartTokenValue}`,
      {
        couponCode: coupon,
        userClientId,
        billingAddress,
        walletId,
      },
      {
        headers: this.ECOM_HEADERS,
      }
    );
  }

  checkoutCart(
    cartTokenValue: string,
    billingAddress: BillingAddress,
    userClientId: string,
    notes: string,
    paymentId: number,
    couponCode?: string | null,
    sendConfirmationEmail?: boolean,
    orderTotal?: number,
    walletId?: string
  ): Observable<void> {
    return this.updateCartCheckoutDetails(
      cartTokenValue,
      billingAddress,
      userClientId,
      couponCode,
      sendConfirmationEmail,
      walletId
    ).pipe(
      mergeMap(() =>
        // if order total is / product is free, skip setting payment method call
        orderTotal === 0 ? of(null) : this.selectPaymentMethod(cartTokenValue, paymentId)
      ),
      mergeMap(() => this.completeCart(cartTokenValue, notes))
    );
  }

  private createCart(accountId: string): Observable<string> {
    return this.http
      .post<{ tokenValue: string }>(
        `${this.environment.api.ecom}/orders`,
        { accountId },
        {
          headers: this.ECOM_HEADERS,
        }
      )
      .pipe(map(({ tokenValue }) => tokenValue));
  }

  private addProductToCart(
    cartTokenValue: string,
    variantCode: string,
    quantity: number
  ): Observable<CartDto> {
    return this.http.post<CartDto>(
      `${this.environment.api.ecom}/orders/${cartTokenValue}/items`,
      {
        productVariant: variantCode,
        quantity,
      },
      {
        headers: this.ECOM_HEADERS,
      }
    );
  }

  private removeProductFromCart(cartTokenValue: string, orderItemId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.environment.api.ecom}/orders/${cartTokenValue}/items/${orderItemId}`,
      {
        headers: this.ECOM_HEADERS,
      }
    );
  }

  private updateCartCheckoutDetails(
    cartTokenValue: string,
    billingAddress: BillingAddress,
    userClientId: string,
    couponCode?: string | null,
    sendConfirmationEmail?: boolean,
    walletId?: string
  ): Observable<string> {
    return this.http
      .put<void>(
        `${this.environment.api.ecom}/orders/${cartTokenValue}`,
        {
          userClientId,
          billingAddress,
          couponCode,
          sendConfirmationEmail,
          walletId,
        },
        {
          headers: this.ECOM_HEADERS,
        }
      )
      .pipe(map(() => cartTokenValue));
  }

  private selectPaymentMethod(cartTokenValue: string, paymentId: number): Observable<void> {
    return this.http.patch<void>(
      `${this.environment.api.ecom}/orders/${cartTokenValue}/payments/${paymentId}`,
      { paymentMethod: 'bank_transfer' },
      {
        headers: this.ECOM_PATCH_HEADERS,
      }
    );
  }

  private completeCart(cartTokenValue: string, notes: string): Observable<void> {
    return this.http.patch<void>(
      `${this.environment.api.ecom}/orders/${cartTokenValue}/complete`,
      { notes },
      {
        headers: this.ECOM_PATCH_HEADERS,
      }
    );
  }
}
